import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import { useAuth } from '../utils/auth';

export default function AdminAccessBoundary({ children }: { children: ReactNode }) {
  const { currentUser, loading, isAdmin, accessError, refreshAccess, logout } = useAuth();
  const [signOutError, setSignOutError] = React.useState(false);
  if (loading) return <Box role="status" aria-label="Checking admin access" sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
  if (!currentUser) return <Navigate to="/" replace />;
  if (isAdmin) return <>{children}</>;
  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h5" component="h1">{accessError ? 'Unable to check access' : 'Admin access needed'}</Typography>
        <Typography sx={{ mt: 2, overflowWrap: 'anywhere' }}>You’re signed in as {currentUser.email}.</Typography>
        <Alert severity={accessError ? 'error' : 'info'} sx={{ my: 2 }}>
          {accessError || 'This account does not have admin permission yet. Ask a workspace owner to enable it, then refresh access.'}
        </Alert>
        {signOutError && <Alert severity="error" sx={{ mb: 2 }}>Could not sign out. Please try again.</Alert>}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={refreshAccess}>Refresh access</Button>
          <Button variant="outlined" onClick={async () => {
            setSignOutError(false);
            try { await logout(); } catch { setSignOutError(true); }
          }}>Sign out</Button>
        </Box>
      </Paper>
    </Container>
  );
}
